import React, { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { App, Tabs, Button, Table, Tooltip, TabsProps, Tag } from 'antd'
import { EditOutlined, DeleteOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

import FormattedDate from '@vacationtracker/shared/components/formatted-date'
import IntlMessages from '../../util/IntlMessages'

import { IHoliday } from '../../types/holidays'
import { IHolidaysByYear } from '../../types/locations'
import { useHistory } from 'react-router'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'

enum ActiveTab {
  pastYear = '3',
  currentYear = '1',
  nextYear = '2'
}
interface ILocationHolidaysProps {
  holidays?: IHolidaysByYear[]
  onDeleteHoliday: (holidayId: string) => Promise<void>
  locationName: string
  locationId: string
  locale: LocaleEnum
}

const LocationHolidays = ({ holidays, onDeleteHoliday, locationName, locationId, locale }: ILocationHolidaysProps) => {
  const { formatMessage } = useIntl()
  const { modal } = App.useApp()
  const [currentYear] = useState(Number(dayjs().format('YYYY')))
  const [nextYear] = useState(dayjs().add(1, 'year').format('YYYY'))
  const [holidaysListCurrentYear, setHolidaysListCurrentYear] = useState<IHoliday[]>([])
  const [holidaysListNextYear, setHolidaysListNextYear] = useState<IHoliday[]>([])
  const [holidaysListPastYear, setHolidaysListPastYear] = useState<IHoliday[]>([])
  const [year, setYear] = useState(Number(dayjs().format('YYYY')))
  const history = useHistory()

  useEffect(() => {
    holidays?.forEach(holidayData => {
      showHolidaysForYears(holidayData.year, holidayData.holidays)
    })
  }, [holidays])

  const handleDeleteHoliday = (holidayId: string, holiday: IHoliday) => {
    modal.confirm({
      title: formatMessage({ id: 'components.locationHolidays.deleteHolidaysTitle' }),
      icon: <ExclamationCircleOutlined />,
      content: <>
        {holiday.recurring && holiday.type === 'CUSTOM' && (
          <>
            {formatMessage({ id: 'components.locationHolidays.deleteHolidaysConfirmRecurring' })}
            <br />
            <br />
          </>
        )}
        {formatMessage({ id: 'components.locationHolidays.deleteHolidaysConfirm' }, { name: holiday.name })}
      </>,
      okType: 'danger',
      okText: formatMessage({ id: 'app.delete' }),
      onOk: async () => {
        await onDeleteHoliday(holidayId)
      },
    })
  }

  let pastHolidays: IHoliday[] = []

  const showHolidaysForYears = (year, holidaysForYear) => {
    holidaysForYear = holidaysForYear.filter(holiday => holiday.date.substring(0,4) < currentYear + 2)    
    if (currentYear === Number(year)) {
      setHolidaysListCurrentYear((holidaysForYear as IHoliday[]).sort((a: IHoliday, b: IHoliday) => dayjs(a.date).format() < dayjs(b.date).format() ? -1 : 1))
    } else if (currentYear + 1 === Number(year)) {
      setHolidaysListNextYear((holidaysForYear as IHoliday[]).sort((a: IHoliday, b: IHoliday) => dayjs(a.date).format() < dayjs(b.date).format() ? -1 : 1))
    } else {
      pastHolidays = [...pastHolidays, ...(holidaysForYear as IHoliday[])].filter((v,i,a)=>a.findIndex(t=>(JSON.stringify(t) === JSON.stringify(v)))===i)
      setHolidaysListPastYear(pastHolidays.sort((a: IHoliday, b: IHoliday) => dayjs(a.date).format() > dayjs(b.date).format() ? -1 : 1))
    }
  }

  const columns = [{
    title: <IntlMessages id="holidays.holidaysName" />,
    dataIndex: 'name',
    key: 'name',
    render: (name: string, row: IHoliday) => {
      return (
        <>{name} <Tag color='orange'>{row.type === 'CUSTOM' ? <IntlMessages id="app.holiday.typeCustom" /> : <IntlMessages id="app.holiday.typeLib" />}</Tag></>
      )
    },
  }, {
    title: <IntlMessages id="components.leavesColumns.dates" />,
    dataIndex: 'date',
    key: 'date',
    // eslint-disable-next-line react/display-name
    render: (date: string, row: IHoliday) => {
      return (
        <>
          <FormattedDate locale={locale} value={date} format='MMMM D' />
          {' '}{row.isHalfDay && <IntlMessages id="components.leavesColumns.halfDayHoliday" />}{' '}
          {row?.multiDayId && row?.endDate && (
            <>
              - <FormattedDate locale={locale} value={row?.endDate} format='MMMM D' />
            </>
          )}
        </>
      )
    },
  }, {
    title: <IntlMessages id="dashboard.days" />,
    dataIndex: 'date',
    key: 'day',
    // eslint-disable-next-line react/display-name
    render: (date: string, row: IHoliday) => {
      return (<>
        <FormattedDate locale={locale} value={date} format="dddd" /> {row.multiDayId && <>- <FormattedDate locale={locale} value={row.endDate as string} format="dddd" /></> }
      </>)
    },
  }, {
    title: (
      <>
        <IntlMessages id="app.recurring" />&nbsp;
        <Tooltip title={<IntlMessages id="holidays.importHolidaysAutomaticallyTooltip" />}><InfoCircleOutlined /></Tooltip>
      </>
    ),
    dataIndex: 'recurring',
    key: 'recurring',
    render: (recurring: boolean) => {
      return (recurring ? <IntlMessages id="app.yes" /> : <IntlMessages id="app.no" />)
    },
  }, {
    title: ' ',
    dataIndex: 'id',
    className:'actions',
    key: 'id',
    // eslint-disable-next-line react/display-name
    render: (id: string, row: IHoliday) => {
      return (<>
        <Tooltip title={<IntlMessages id="components.addHolidayForm.editHoliday" />}>
          <Button type="link" onClick={() => history.push(`/app/holiday/${locationId}/${row.id}`)}>
            <EditOutlined style={{ color: '#87d068' }} />
          </Button>
        </Tooltip>
        <Tooltip title={<IntlMessages id="components.locationHolidays.deleteHolidaysTitle" />}>
          <Button type="link" onClick={() => handleDeleteHoliday(row.id, row)}>
            <DeleteOutlined style={{ color: '#f50' }} />
          </Button>
        </Tooltip>
      </>)
    },
  }]

  const locationTabs: TabsProps['items'] = [
    {
      key: ActiveTab.currentYear,
      label: formatMessage({ id: 'holidays.holidaysTab' }, { year: currentYear }),
      children: <Table
        dataSource={holidaysListCurrentYear}
        columns={columns}
        rowKey={record => `${record.date}-${record.name}`}
        pagination={false}
      />,
    },
    {
      key: ActiveTab.nextYear,
      label: formatMessage({ id: 'holidays.holidaysTab' }, { year: nextYear }),
      children: <Table
        dataSource={holidaysListNextYear}
        columns={columns}
        rowKey={record => `${record.date}-${record.name}`}
        pagination={false}
      />,
    },
    {
      key: ActiveTab.pastYear,
      label: formatMessage({ id: 'holidays.pastHolidays' }),
      children: <Table
        dataSource={holidaysListPastYear}
        columns={columns}
        rowKey={record => `${record.date}-${record.name}`}
        pagination={false}
      />,
    },
  ]

  return (
    <>
      <IntlMessages id="components.locationHolidays.holidaysForLocation" values={{ locationName }} />
      <Tabs
        onChange={(activeKey) => {
          switch (activeKey) {
            case ActiveTab.currentYear:
              setYear(currentYear)
              break
            case ActiveTab.nextYear:
              setYear(Number(nextYear))
              break
            case ActiveTab.pastYear:
              setYear(Number(dayjs().subtract(1, 'year').format('YYYY')))
              break
            default:
              setYear(Number(dayjs().format('YYYY')))
          }
        }}
        defaultActiveKey="1"
        tabBarExtraContent={<>
          <Button
            type="default"
            onClick={() => history.push(`/app/import-holidays/${locationId}/${year}`)}
            style={{ marginRight: 5 }}
            className="joyride-import-holidays"
          >
            <IntlMessages id="holidays.importHolidays" />
          </Button>
          <Button
            type="default"
            onClick={() => history.push(`/app/holiday/${locationId}`)}
          >
            <IntlMessages id="components.locationHolidays.addHolidays" />
          </Button>
        </>}
        items={locationTabs}
      />
    </>
  )
}

export default LocationHolidays
